// BeziehungsTabelle.tsx — die Beziehungslisten der Kontaktanlage im Aussehen der
// Verknüpfungen der Kontaktakte (redesign/ContactConnections: `.relsec` mit
// Überschrift + „Hinzufügen" rechts, darunter die `.kt`-Tabelle mit
// Kopfzeile, Name fett, Rolle, Aktionen Stift/Eimer rechts; User 28.09.2026).
//
// Die Anlage läuft nicht im `.kvp-rd`-Scope, deshalb die Optik hier als
// makeStyles mit denselben Maßen statt der CSS-Klassen der Akte.
//
// Reine Anzeige: Was ein Klick bewirkt, entscheidet der Aufrufer (EintragsListe,
// MenschenFuersUnternehmenList). Dazu optional Ziehen & Ablegen zwischen zwei
// Tabellen (Weitere Beziehungen ⇄ Haushalt).
//
// Spalten wie in der Akte (User 28.09.2026): Name · Rolle · [Anteile] ·
// Kontaktmöglichkeit · Kontaktstatus. Was die Anlage nicht weiß (ein noch nicht
// gespeicherter Kontakt), bleibt leer.
//
// Gesperrte Akte (`<fieldset disabled>` in AkteAllgemeinAnlage): Das sperrt
// JEDEN <button> darunter — auch die, die nur etwas ZEIGEN. Deshalb sind der
// Stift und die Kontaktmöglichkeit hier keine <button>, sondern Elemente mit
// role="button": Sie bleiben klickbar, und der Stift öffnet in der gesperrten
// Akte das Popup zum Ansehen (ohne „Übernehmen"). Nur was ändert (X, Eimer,
// Verschieben, „+"), bleibt ein echter Knopf und damit gesperrt.
//
// X und Eimer (Plan 2026-10-01, Paket A2, User 02.10.2026): Das X entfernt nur
// die Verbindung aus der Liste (wirkt beim Speichern). Der Eimer löscht den
// Gegenkontakt selbst — nur in der Akte, nur für gespeicherte Gegenkontakte
// und nur mit Löschrecht (relationsInline/kontaktLoeschen.ts).
//
// Handy (≤ 600px, T-63): Das Desktop-Raster quetschte fünf bis sieben Spalten
// in 400px — Rolle zerfiel in Silben, Status und Aktionen lagen übereinander.
// Dort wird jede Zeile zur Karte: links Name, Rolle und die übrigen Zellen
// untereinander (mit kleinem Titel aus `data-titel`, weil die Kopfzeile
// entfällt), rechts oben die Aktionen. Die Spaltenvorlage steht deshalb als
// CSS-Variable `--bt-spalten` statt als Inline-`gridTemplateColumns` — ein
// Inline-Wert würde die Media Query überstimmen.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { KontaktmoeglichkeitModal } from "../redesign/KontaktmoeglichkeitPopup";
import type { ContactDetailsFormValues } from "../../types/Contact";
import "../redesign/contactRedesign.css";
import { KontaktName } from "./KontaktSprung";
import { SofortTipp } from "./SofortTipp";
import { useKontaktLoeschen } from "../relationsInline/kontaktLoeschen";
import { ziehAutoScrollEinrichten } from "./ziehAutoScroll";

// Gesperrtes Formular / Klickfläche ohne <button>: eigenes Modul, damit auch
// die Feld-Widgets sie nutzen können, ohne diese Tabelle mitzuziehen.
import { AnsichtsKnopf, imGesperrtenFormular } from "./AnsichtsKnopf";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";
export { AnsichtsKnopf, imGesperrtenFormular };

/** Kontaktmöglichkeit einer Zeile — nur bei einem gespeicherten Kontakt. */
export type ZeilenKontakt = {
  contactId?: number;
  name: string;
  typ?: "Person" | "Firma";
  /**
   * Erste Telefonnummer bzw. E-Mail, falls bekannt (aus relations-deep).
   * `undefined` = unbekannt (Symbol trotzdem zeigen, das Popup lädt nach),
   * `null`/"" = der Kontakt hat keine.
   */
  telefon?: string | null;
  email?: string | null;
};

export type BeziehungsZeile = {
  key: React.Key;
  name: React.ReactNode;
  rolle?: React.ReactNode;
  /** Kleiner Zusatz unter dem Namen (z. B. „aus Firmenverbindungen"). */
  hinweis?: React.ReactNode;
  /** Ausgeschieden o. ä. — Name und Rolle durchgestrichen. */
  durchgestrichen?: boolean;
  /** Spalte „Anteile" (nur wenn die Tabelle sie führt). */
  anteile?: React.ReactNode;
  /** Spalte „Kontaktmöglichkeit". */
  kontakt?: ZeilenKontakt;
  /** Spalte „Kontaktstatus" (Primär-Status des verknüpften Kontakts). */
  status?: string;
  /**
   * Stift. `nurAnsehen` ist gesetzt, wenn die Zeile in einem gesperrten
   * Formular steht — dann soll das Popup nur zeigen.
   */
  onBearbeiten?: (opts: { nurAnsehen: boolean }) => void;
  /**
   * Klick auf die Rolle (User 30.09.2026): öffnet z. B. das Popup
   * „Verantwortung fürs Unternehmen". Klickbar auch im gesperrten Formular.
   */
  onRolle?: (opts: { nurAnsehen: boolean }) => void;
  /** Die Zeile wird gerade inline bearbeitet — der Stift beendet das. */
  inlineAktiv?: boolean;
  onEntfernen?: () => void;
  /** Weitere Aktion(en) links neben Stift/Eimer (z. B. „In den Haushalt"). */
  extra?: React.ReactNode;
  /** Ziehbar: dieser Text wird beim Ablegen an `ablage.onAblage` übergeben. */
  ziehDaten?: string;
  /**
   * Zusätzliche Kennzeichen der gezogenen Zeile (je als eigener MIME-Typ
   * `application/x-kmvs-<marker>`). Beim Überfahren sind nur die TYPEN lesbar,
   * nicht die Daten — darüber entscheidet ein Ziel, ob es annimmt (Paket A6).
   */
  ziehMarker?: string[];
  /** Ablage auf GENAU diese Zeile (Paket A7: Person auf eine Firma ziehen). */
  ablage?: ZeilenAblage;
};

/** Ablage auf eine einzelne Zeile. */
export type ZeilenAblage = {
  /** Angenommene Arten (MIME-Suffixe). */
  arten: string[];
  onAblage: (daten: string, art: string) => void;
  /** Text an der Zeile, solange etwas Passendes darüber schwebt. */
  hinweis?: string;
  /** Auch in der gesperrten Akte annehmen (z. B. weil sofort gespeichert wird). */
  auchGesperrt?: boolean;
};

/**
 * Ablage auf die ganze Tabelle. `art`/`arten`: angenommene Arten (Standard:
 * `ziehArt`). `pruefen` entscheidet beim Überfahren anhand der MIME-Typen:
 * `true` = annehmen, Text = ablehnen (wird angezeigt), `null` = gar nicht
 * reagieren (etwa die eigene Liste).
 */
export type TabellenAblage = {
  onAblage: (daten: string, art: string) => void;
  hinweis?: string;
  art?: string;
  arten?: string[];
  pruefen?: (typen: readonly string[]) => true | string | null;
  /** Auch in der gesperrten Akte annehmen. Standard: nein — Ablegen ändert das Formular. */
  auchGesperrt?: boolean;
};

export type BeziehungsTabelleProps = {
  /** Überschrift der Liste (wie `.relsec-h h3`). Ohne: keine Kopfzeile. */
  titel?: React.ReactNode;
  untertitel?: React.ReactNode;
  /** Knopf rechts neben der Überschrift, z. B. „+ Verbindung zu Unternehmen". */
  aktion?: React.ReactNode;
  /**
   * Spaltenköpfe. `anteile` nur, wenn gesetzt; Kontaktmöglichkeit und
   * Kontaktstatus stehen immer da, außer sie sind mit `false` abgeschaltet.
   */
  spalten?: BeziehungsSpalten;
  zeilen: BeziehungsZeile[];
  leerText?: string;
  /** Art der ziehbaren Zeilen (MIME-Suffix) — Ziehen und Ablegen müssen übereinstimmen. */
  ziehArt?: string;
  /**
   * Diese Tabelle nimmt gezogene Zeilen an — der Art `ablage.art`, sonst der
   * Art `ziehArt` (Haushalt ⇄ weitere Beziehungen ziehen in BEIDE Richtungen,
   * je Richtung mit eigener Art, damit eine Liste sich nicht selbst annimmt).
   */
  ablage?: TabellenAblage;
  /**
   * Weitere Spalten vor den Aktionen (z. B. „Mensch im Unternehmen": Telefon,
   * E-Mail, Abteilung, bAV … wie in der früheren Akte).
   */
  zusatzSpalten?: ZusatzSpalte[];
};

export type ZusatzSpalte = {
  key: string;
  titel: React.ReactNode;
  /** CSS-Grid-Breite, z. B. "120px" oder "minmax(0, 1fr)". */
  breite?: string;
  /** Inhalt mittig (Ankreuzfelder). */
  mittig?: boolean;
  zelle: (zeile: BeziehungsZeile) => React.ReactNode;
};

export type BeziehungsSpalten = {
  name?: string;
  rolle?: string;
  anteile?: string;
  kontakt?: string | false;
  status?: string | false;
};

const MIME = (art: string) => `application/x-kmvs-${art}`;
/** MIME-Typ einer Ziehart — geteilt mit den Aufrufern (Kennzeichen prüfen). */
export const ziehMime = MIME;
/**
 * Ablage-Effekt, den die Quelle erlaubt. Ein `dropEffect`, der nicht zu
 * `effectAllowed` der Quelle passt, lässt den Browser das Ablegen still
 * verweigern — kein `drop`-Ereignis (Befund DEV 02.10.2026: Zeilen ziehen mit
 * „move", die Firmenzeile meldete „copy" → Person auf Firma ging nie).
 */
export function passenderDropEffect(dt: DataTransfer | null | undefined): "move" | "copy" | "link" {
  const erlaubt = (dt?.effectAllowed ?? "all").toLowerCase();
  if (erlaubt === "all" || erlaubt === "uninitialized" || erlaubt.includes("move")) return "move";
  if (erlaubt.includes("copy")) return "copy";
  return "link";
}

/** Hinweis, wenn in der gesperrten Akte etwas abgelegt werden soll. */
export const GESPERRT_ABLAGE = "Gesperrt — zum Ändern das Schloss öffnen.";

/** Handy-Breite wie in formPrimitives. */
const HANDY = "@media (max-width: 600px)";

/** Spaltentitel als Text für `data-titel` — nur, wenn er einer ist. */
const titelText = (t: React.ReactNode): string | undefined => (typeof t === "string" && t ? t : undefined);

const useStyles = makeStyles({
  sektion: { display: "flex", flexDirection: "column", rowGap: "12px" },
  kopf: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", columnGap: "12px" },
  kopfText: { display: "flex", flexDirection: "column", rowGap: "2px", minWidth: 0 },
  titel: {
    margin: 0,
    fontFamily: REFINED.fontUI,
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
    color: REFINED.ink,
  },
  untertitel: { margin: 0, fontFamily: REFINED.fontUI, fontWeight: 500, fontSize: "13.5px", lineHeight: 1.4, color: REFINED.ink2 },
  tabelle: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
    ...shorthands.overflow("hidden"),
    backgroundColor: REFINED.white,
    transitionProperty: "box-shadow, border-color",
    transitionDuration: "120ms",
  },
  ablageAktiv: { ...shorthands.borderColor(REFINED.aub), boxShadow: `inset 0 0 0 2px ${REFINED.aub}` },
  // Ein Ziel, das die gezogene Zeile NICHT annimmt (Paket A6): gestrichelt rot.
  ablageAbgelehnt: {
    ...shorthands.borderColor(REFINED.err),
    ...shorthands.borderStyle("dashed"),
    boxShadow: "none",
  },
  ablageAbgelehntHinweis: {
    ...shorthands.padding("8px", "16px"),
    fontFamily: REFINED.fontUI,
    fontSize: "12.5px",
    fontWeight: 600,
    color: REFINED.err,
    backgroundColor: REFINED.errBg,
  },
  zeileAblage: { boxShadow: `inset 0 0 0 2px ${REFINED.aub}`, backgroundColor: REFINED.aub50 },
  zeileAblageHinweis: {
    gridColumn: "1 / -1",
    fontFamily: REFINED.fontUI,
    fontSize: "12.5px",
    fontWeight: 600,
    color: REFINED.aub,
  },
  raster: {
    display: "grid",
    gridTemplateColumns: "var(--bt-spalten, minmax(0, 1.1fr) minmax(0, 1.3fr) auto)",
    alignItems: "center",
    columnGap: "16px",
    [HANDY]: {
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gridAutoFlow: "row",
      rowGap: "6px",
      columnGap: "8px",
      alignItems: "start",
    },
  },
  kopfzeile: {
    ...shorthands.padding("12px", "16px"),
    ...shorthands.borderBottom("1.5px", "solid", REFINED.line),
    fontFamily: REFINED.fontUI,
    fontWeight: 700,
    fontSize: "13.5px",
    letterSpacing: "0.02em",
    color: REFINED.ink2,
    whiteSpace: "nowrap",
    [HANDY]: { display: "none" },
  },
  zeile: {
    ...shorthands.padding("11px", "16px"),
    fontFamily: REFINED.fontUI,
    fontSize: "14.5px",
    color: REFINED.ink,
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    ":not(:last-child)": { ...shorthands.borderBottom("1px", "solid", REFINED.line) },
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  ziehbar: { cursor: "grab" },
  name: {
    fontWeight: 600,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    [HANDY]: { gridColumn: "1" },
  },
  nameText: {
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    // Auf dem Handy umbrechen statt auf „…" schrumpfen.
    [HANDY]: { whiteSpace: "normal", overflowWrap: "break-word" },
  },
  hinweis: { fontWeight: 500, fontSize: "12px", color: REFINED.ink3 },
  rolle: {
    minWidth: 0,
    color: REFINED.ink,
    overflowWrap: "anywhere",
    // Ganze Wörter statt Silben: „Haushaltsmitglied", nicht „Haushalts-mitgl-ied".
    [HANDY]: { gridColumn: "1", overflowWrap: "break-word", color: REFINED.ink2 },
  },
  rolleKnopf: {
    cursor: "pointer",
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationColor: REFINED.ink3,
    textUnderlineOffset: "3px",
    ...shorthands.borderRadius("4px"),
    ":hover": { color: REFINED.aub, textDecorationColor: REFINED.aub },
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: REFINED.aub },
  },
  zeileInline: { backgroundColor: REFINED.aub50 },
  iconAktiv: { backgroundColor: REFINED.aub50, color: REFINED.aub },
  leerStrich: { color: REFINED.ink3 },
  gestrichen: { textDecorationLine: "line-through", color: REFINED.ink3 },
  aktionen: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    columnGap: "4px",
    // Die Aktionsspalte liegt immer obenauf: Ragt doch einmal ein Inhalt aus
    // einer Nachbarzelle herein, verdeckt die Spalte ihn (in der Zeilenfarbe,
    // auch beim Überfahren) statt umgekehrt (Plan 2026-10-01, Paket A1).
    position: "relative",
    zIndex: 1,
    backgroundColor: "inherit",
    // Rechts oben neben Name und Rolle (über beide Zeilen, sonst stünde die
    // Rolle eine Knopfhöhe unter dem Namen); die übrigen Zellen fließen darunter.
    [HANDY]: { gridColumn: "2", gridRow: "1 / span 2", alignSelf: "start", flexWrap: "wrap", maxWidth: "128px" },
  },
  icon: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.ink3,
    cursor: "pointer",
    display: "inline-grid",
    placeItems: "center",
    width: "34px",
    height: "34px",
    ...shorthands.borderRadius("9px"),
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": { backgroundColor: REFINED.aub50, color: REFINED.aub },
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: REFINED.aub },
    // Fingerbreite Trefferfläche.
    [HANDY]: { width: "40px", height: "40px" },
  },
  iconWeg: {
    ":hover": { backgroundColor: REFINED.errBg, color: REFINED.err },
    ":disabled": { cursor: "default", opacity: 0.45 },
    ":disabled:hover": { backgroundColor: "transparent", color: REFINED.ink3 },
  },
  zelle: {
    minWidth: 0,
    color: REFINED.ink,
    overflowWrap: "anywhere",
    [HANDY]: { gridColumn: "1", overflowWrap: "break-word" },
  },
  /** Ankreuzfelder mittig (Zusatzspalten mit `mittig`) — auf dem Handy linksbündig unter dem Titel. */
  zelleMittig: {
    display: "flex",
    justifyContent: "center",
    [HANDY]: { flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" },
  },
  /** Spaltentitel vor dem Inhalt — nur auf dem Handy, wo die Kopfzeile fehlt. */
  mitTitel: {
    [HANDY]: {
      "::before": {
        content: "attr(data-titel)",
        display: "block",
        fontSize: "11.5px",
        fontWeight: 600,
        lineHeight: 1.3,
        color: REFINED.ink3,
        marginBottom: "2px",
      },
    },
  },
  komm: { display: "inline-flex", columnGap: "4px" },
  kommIcon: {
    display: "inline-grid",
    placeItems: "center",
    width: "30px",
    height: "30px",
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#eaf1f8",
    color: "#3a6ea5",
    boxShadow: "0 1px 3px rgba(58,110,165,.22)",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "130ms",
    ":hover": { backgroundColor: "#3a6ea5", color: REFINED.white },
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: REFINED.aub },
  },
  // Lange Status (z. B. „Kunde über Muttergesellschaft") ragten mit `nowrap`
  // in die Aktionsspalte. Jetzt endet der Chip am Zellrand mit „…"; den vollen
  // Text zeigt der SofortTipp (Plan 2026-10-01, Paket A1).
  chip: {
    display: "inline-block",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
    verticalAlign: "middle",
    ...shorthands.padding("3px", "10px"),
    ...shorthands.borderRadius("999px"),
    fontWeight: 600,
    fontSize: "12.5px",
    lineHeight: 1.4,
    whiteSpace: "nowrap",
  },
  chipKunde: { backgroundColor: REFINED.aub50, color: REFINED.aub700 },
  chipInteressent: { backgroundColor: "#eef4ec", color: "#3c6e3c" },
  chipSonstige: { backgroundColor: REFINED.fieldBg, color: REFINED.ink2 },
  leer: {
    ...shorthands.padding("14px", "16px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    color: REFINED.ink3,
    fontStyle: "italic",
  },
  ablageHinweis: {
    ...shorthands.padding("8px", "16px"),
    fontFamily: REFINED.fontUI,
    fontSize: "12.5px",
    fontWeight: 600,
    color: REFINED.aub,
    backgroundColor: REFINED.aub50,
  },
});

/** „+ Hinzufügen"-Knopf im Kopf der Liste — wie `.rel-add` der Akte. */
const useKnopfStyles = makeStyles({
  knopf: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    flexShrink: 0,
    ...shorthands.border("1.5px", "solid", REFINED.line),
    backgroundColor: REFINED.white,
    color: REFINED.aub700,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: 1,
    ...shorthands.padding("8px", "13px"),
    ...shorthands.borderRadius("9px"),
    whiteSpace: "nowrap",
    transitionProperty: "background-color, border-color",
    transitionDuration: "130ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), backgroundColor: REFINED.aub50 },
  },
});

export function HinzufuegenKnopf({ label, onClick }: { label: string; onClick: () => void }) {
  const s = useKnopfStyles();
  return (
    <button type="button" className={s.knopf} onClick={onClick}>
      + {label}
    </button>
  );
}

export const StiftIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13.5 4.5l2 2M4 16l1-3.5 8-8a1.4 1.4 0 012 2l-8 8L4 16z" />
  </svg>
);
/** X = Verbindung aus der Liste entfernen (wirkt beim Speichern). */
export const EntfernenIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />
  </svg>
);
/** Eimer = Kontakt löschen (Papierkorb). */
export const EimerIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 6h12M8 6V4.5A1.5 1.5 0 019.5 3h1A1.5 1.5 0 0112 4.5V6M6 6l.7 9a1.5 1.5 0 001.5 1.4h3.6a1.5 1.5 0 001.5-1.4L15 6M8.5 9v4M11.5 9v4" />
  </svg>
);

/** Kleiner Aktionsknopf in Zeilen-Optik (für `BeziehungsZeile.extra`). */
export function ZeilenAktion({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const s = useStyles();
  return (
    <SofortTipp text={label} ausrichtung="rechts">
      <button type="button" className={s.icon} aria-label={label} onClick={onClick}>
        {children}
      </button>
    </SofortTipp>
  );
}

const TelefonIcon = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M7 3.8L4.5 4.1C3.5 7 5.3 10.7 7.8 13.2s6.2 4.3 9.1 3.3l.3-2.7-3.1-1.4-1.4 1.4a10 10 0 01-3.4-3.4l1.4-1.4z" />
  </svg>
);
const MailIcon = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
    <path d="M3 6l7 5 7-5" />
  </svg>
);

/** Telefon/E-Mail wie in der Akte: zwei Symbole, ein Klick öffnet das Popup. */
function KontaktZelle({ kontakt }: { kontakt?: ZeilenKontakt }) {
  const s = useStyles();
  const [modus, setModus] = React.useState<"phone" | "email" | null>(null);
  if (!kontakt?.contactId) return <span className={s.leerStrich}>—</span>;
  const telefonDa = kontakt.telefon !== null && kontakt.telefon !== "";
  const mailDa = kontakt.email !== null && kontakt.email !== "";
  if (!telefonDa && !mailDa) return <span className={s.leerStrich}>—</span>;
  const row = {
    kontaktId: kontakt.contactId,
    typ: kontakt.typ ?? "Person",
    displayName: kontakt.name,
  } as unknown as ContactDetailsFormValues;
  return (
    <span className={s.komm}>
      {telefonDa && (
        <AnsichtsKnopf
          label={kontakt.telefon ? `Telefonnummern (${kontakt.telefon})` : "Telefonnummern"}
          className={s.kommIcon}
          onClick={() => setModus("phone")}
        >
          {TelefonIcon}
        </AnsichtsKnopf>
      )}
      {mailDa && (
        <AnsichtsKnopf
          label={kontakt.email ? `E-Mail-Adressen (${kontakt.email})` : "E-Mail-Adressen"}
          className={s.kommIcon}
          onClick={() => setModus("email")}
        >
          {MailIcon}
        </AnsichtsKnopf>
      )}
      {modus && (
        <KontaktmoeglichkeitModal row={row} mode={modus} onMode={setModus} onClose={() => setModus(null)} />
      )}
    </span>
  );
}

function StatusZelle({ status }: { status?: string }) {
  const s = useStyles();
  if (!status) return <span className={s.leerStrich}>—</span>;
  const ton = status === "Kunde" ? s.chipKunde : status === "Interessent" ? s.chipInteressent : s.chipSonstige;
  return (
    <SofortTipp text={status} nurWennGekuerzt>
      <span className={mergeClasses(s.chip, ton)} data-status-chip="">
        {status}
      </span>
    </SofortTipp>
  );
}

/**
 * Eine Zeile samt Ziehen (Quelle) und — optional — Ablage auf genau diese
 * Zeile (Paket A7). Die Zeilen-Ablage hält das Ereignis an, damit die Ablage
 * der ganzen Tabelle nicht zusätzlich greift.
 */
function ZeilenRahmen({
  zeile: z,
  mime,
  rasterStil,
  className,
  ablageClass,
  hinweisClass,
  children,
}: {
  zeile: BeziehungsZeile;
  mime: string | null;
  rasterStil: React.CSSProperties;
  className: string;
  ablageClass: string;
  hinweisClass: string;
  children: React.ReactNode;
}) {
  const [ueber, setUeber] = React.useState(false);
  const za = z.ablage;
  // Eine inline bearbeitete Zeile (Eingabefelder) zieht nicht — sonst wird
  // aus dem Markieren von Text im Feld ein Ziehen der Zeile.
  const ziehbar = !!(z.ziehDaten && mime) && !z.inlineAktiv;
  const passendeArt = (e: React.DragEvent): string | null => {
    if (!za) return null;
    const typen = Array.from(e.dataTransfer?.types ?? []);
    const art = za.arten.map(MIME).find((m) => typen.includes(m));
    if (!art) return null;
    if (!za.auchGesperrt && imGesperrtenFormular(e.currentTarget as Element)) return null;
    return art;
  };
  return (
    <div
      role="listitem"
      className={mergeClasses(className, ueber && ablageClass)}
      style={rasterStil}
      data-zeilen-ablage={za ? "" : undefined}
      draggable={ziehbar || undefined}
      onDragStart={
        ziehbar && mime
          ? (e) => {
              e.dataTransfer.setData(mime, z.ziehDaten!);
              for (const m of z.ziehMarker ?? []) e.dataTransfer.setData(MIME(m), "1");
              e.dataTransfer.effectAllowed = "move";
            }
          : undefined
      }
      onDragOver={
        za
          ? (e) => {
              if (!passendeArt(e)) return;
              e.preventDefault();
              e.stopPropagation();
              if (e.dataTransfer) e.dataTransfer.dropEffect = passenderDropEffect(e.dataTransfer);
              if (!ueber) setUeber(true);
            }
          : undefined
      }
      onDragEnter={
        za
          ? (e) => {
              // Auch dragenter freigeben — manche Browser entscheiden schon hier.
              if (!passendeArt(e)) return;
              e.preventDefault();
              e.stopPropagation();
              if (e.dataTransfer) e.dataTransfer.dropEffect = passenderDropEffect(e.dataTransfer);
              if (!ueber) setUeber(true);
            }
          : undefined
      }
      onDragLeave={
        za
          ? (e) => {
              const nach = e.relatedTarget as Node | null;
              if (nach && (e.currentTarget as Node).contains(nach)) return;
              setUeber(false);
            }
          : undefined
      }
      onDrop={
        za
          ? (e) => {
              setUeber(false);
              const art = passendeArt(e);
              if (!art) return;
              e.preventDefault();
              e.stopPropagation();
              const daten = e.dataTransfer.getData(art);
              if (daten) za.onAblage(daten, art.replace("application/x-kmvs-", ""));
            }
          : undefined
      }
    >
      {children}
      {ueber && za?.hinweis && <span className={hinweisClass}>{za.hinweis}</span>}
    </div>
  );
}

export function BeziehungsTabelle({
  titel,
  untertitel,
  aktion,
  spalten,
  zeilen,
  leerText,
  ziehArt,
  ablage,
  zusatzSpalten = [],
}: BeziehungsTabelleProps) {
  const s = useStyles();
  const [ueber, setUeber] = React.useState(false);
  const mime = ziehArt ? MIME(ziehArt) : null;
  const kontaktLoeschenFuer = useKontaktLoeschen();
  // Weiterscrollen beim Ziehen nahe am Rand (ein Satz Listener für alle Tabellen).
  React.useEffect(() => ziehAutoScrollEinrichten(), []);
  const loeschenVon = (z: BeziehungsZeile) => kontaktLoeschenFuer(z.kontakt);

  // Eine gemeinsame Breite für die Aktionsspalte aller Zeilen — jede Zeile ist
  // ein eigenes Raster, ohne feste Breite stünde „Rolle" je Zeile anders.
  const aktionsBreite = Math.max(
    76,
    ...zeilen.map(
      (z) => ((z.extra ? 1 : 0) + (z.onBearbeiten ? 1 : 0) + (z.onEntfernen ? 1 : 0) + (loeschenVon(z) ? 1 : 0)) * 38,
    ),
  );
  const mitAnteilen = !!spalten?.anteile;
  const mitKontakt = spalten?.kontakt !== false;
  const mitStatus = spalten?.status !== false;
  const rasterStil = {
    "--bt-spalten": [
      "minmax(0, 1.1fr)",
      "minmax(0, 1.3fr)",
      mitAnteilen ? "minmax(0, 0.55fr)" : null,
      mitKontakt ? "140px" : null,
      mitStatus ? "minmax(0, 0.7fr)" : null,
      ...zusatzSpalten.map((z) => z.breite ?? "minmax(0, 0.8fr)"),
      `${aktionsBreite}px`,
    ]
      .filter(Boolean)
      .join(" "),
  } as React.CSSProperties;
  const titelAnteile = titelText(spalten?.anteile);
  const titelKontakt = typeof spalten?.kontakt === "string" ? spalten.kontakt : "Kontaktmöglichkeit";
  const titelStatus = typeof spalten?.status === "string" ? spalten.status : "Kontaktstatus";

  const ablageArten = ablage
    ? (ablage.arten ?? (ablage.art ? [ablage.art] : ziehArt ? [ziehArt] : [])).map(MIME)
    : [];
  const [abgelehnt, setAbgelehnt] = React.useState<string | null>(null);
  /**
   * Was passiert, wenn das Gezogene hier landet? `art` = angenommene MIME-Art,
   * `grund` = abgelehnt (mit Text), `null` = gar nicht reagieren.
   */
  const bewerte = (e: React.DragEvent): { art: string } | { grund: string } | null => {
    if (!ablage) return null;
    const typen = Array.from(e.dataTransfer?.types ?? []);
    const art = ablageArten.find((m) => typen.includes(m));
    if (!art) return null;
    const urteil = ablage.pruefen ? ablage.pruefen(typen) : true;
    if (urteil === null) return null;
    if (urteil !== true) return { grund: urteil };
    if (!ablage.auchGesperrt && imGesperrtenFormular(e.currentTarget as Element)) return { grund: GESPERRT_ABLAGE };
    return { art };
  };

  const ablageProps = ablage
    ? {
        onDragOver: (e: React.DragEvent) => {
          const b = bewerte(e);
          if (!b) return;
          if ("grund" in b) {
            // Kein preventDefault: Der Browser zeigt „nicht erlaubt".
            if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
            if (abgelehnt !== b.grund) setAbgelehnt(b.grund);
            if (ueber) setUeber(false);
            return;
          }
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = passenderDropEffect(e.dataTransfer);
          if (!ueber) setUeber(true);
          if (abgelehnt) setAbgelehnt(null);
        },
        onDragEnter: (e: React.DragEvent) => {
          const b = bewerte(e);
          if (!b) return;
          if ("grund" in b) {
            if (abgelehnt !== b.grund) setAbgelehnt(b.grund);
            return;
          }
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = passenderDropEffect(e.dataTransfer);
          if (!ueber) setUeber(true);
          if (abgelehnt) setAbgelehnt(null);
        },
        onDragLeave: (e: React.DragEvent) => {
          // Nur beim Verlassen des Containers, nicht beim Wechsel zwischen Kindern.
          const nach = e.relatedTarget as Node | null;
          if (nach && (e.currentTarget as Node).contains(nach)) return;
          setUeber(false);
          setAbgelehnt(null);
        },
        onDrop: (e: React.DragEvent) => {
          setUeber(false);
          setAbgelehnt(null);
          const b = bewerte(e);
          if (!b || "grund" in b) return;
          e.preventDefault();
          const daten = e.dataTransfer.getData(b.art);
          if (daten) ablage.onAblage(daten, b.art.replace("application/x-kmvs-", ""));
        },
      }
    : {};

  // Ein Zug endet auch außerhalb (Esc, daneben losgelassen) — dann die Markierung weg.
  React.useEffect(() => {
    if (!ueber && !abgelehnt) return;
    const weg = () => {
      setUeber(false);
      setAbgelehnt(null);
    };
    alleFenster.addEventListener("dragend", weg);
    alleFenster.addEventListener("drop", weg);
    return () => {
      alleFenster.removeEventListener("dragend", weg);
      alleFenster.removeEventListener("drop", weg);
    };
  }, [ueber, abgelehnt]);

  const leerOderTabelle =
    zeilen.length === 0 ? (
      <div className={mergeClasses(s.tabelle, ueber && s.ablageAktiv, !!abgelehnt && s.ablageAbgelehnt)} {...ablageProps}>
        {leerText && <div className={s.leer}>{leerText}</div>}
        {ueber && ablage?.hinweis && <div className={s.ablageHinweis}>{ablage.hinweis}</div>}
        {abgelehnt && (
          <div className={s.ablageAbgelehntHinweis} role="status">
            {abgelehnt}
          </div>
        )}
      </div>
    ) : (
      <div className={mergeClasses(s.tabelle, ueber && s.ablageAktiv, !!abgelehnt && s.ablageAbgelehnt)} {...ablageProps}>
        <div className={mergeClasses(s.raster, s.kopfzeile)} style={rasterStil} aria-hidden>
          <span>{spalten?.name ?? "Name"}</span>
          <span>{spalten?.rolle ?? "Rolle"}</span>
          {mitAnteilen && <span>{spalten?.anteile}</span>}
          {mitKontakt && <span>{titelKontakt}</span>}
          {mitStatus && <span>{titelStatus}</span>}
          {zusatzSpalten.map((z) => (
            <span key={z.key} style={z.mittig ? { textAlign: "center" } : undefined}>
              {z.titel}
            </span>
          ))}
          <span />
        </div>
        <div role="list">
          {zeilen.map((z) => (
            <ZeilenRahmen
              key={z.key}
              zeile={z}
              mime={mime}
              rasterStil={rasterStil}
              className={mergeClasses(
                s.raster,
                s.zeile,
                z.ziehDaten && mime ? s.ziehbar : undefined,
                z.inlineAktiv ? s.zeileInline : undefined,
              )}
              ablageClass={s.zeileAblage}
              hinweisClass={s.zeileAblageHinweis}
            >
              <span className={s.name} data-zelle="name">
                {/* Klick auf den Namen öffnet die Akte des Kontakts (nur in der Akte, nur mit id). */}
                <KontaktName
                  contactId={z.kontakt?.contactId}
                  className={mergeClasses(s.nameText, z.durchgestrichen && s.gestrichen)}
                >
                  {z.name}
                </KontaktName>
                {z.hinweis && <span className={s.hinweis}>{z.hinweis}</span>}
              </span>
              <span className={mergeClasses(s.rolle, z.durchgestrichen && s.gestrichen)} data-zelle="rolle">
                {z.onRolle ? (
                  // Kein <button>: Auch in der gesperrten Akte soll die Rolle das
                  // Popup öffnen (dort mit „Daten ändern", User 30.09.2026).
                  <AnsichtsKnopf
                    label={typeof z.rolle === "string" && z.rolle ? `Rolle „${z.rolle}“ öffnen` : "Rolle öffnen"}
                    className={s.rolleKnopf}
                    onClick={(el) => z.onRolle!({ nurAnsehen: imGesperrtenFormular(el) })}
                  >
                    {z.rolle ? z.rolle : "—"}
                  </AnsichtsKnopf>
                ) : z.rolle ? (
                  z.rolle
                ) : (
                  <span className={s.leerStrich}>—</span>
                )}
              </span>
              {mitAnteilen && (
                <span
                  className={mergeClasses(s.zelle, titelAnteile && s.mitTitel, z.durchgestrichen && s.gestrichen)}
                  data-titel={titelAnteile}
                >
                  {z.anteile ? z.anteile : <span className={s.leerStrich}>—</span>}
                </span>
              )}
              {mitKontakt && (
                <span className={mergeClasses(s.zelle, s.mitTitel)} data-titel={titelKontakt}>
                  <KontaktZelle kontakt={z.kontakt} />
                </span>
              )}
              {mitStatus && (
                <span className={mergeClasses(s.zelle, s.mitTitel)} data-titel={titelStatus}>
                  <StatusZelle status={z.status} />
                </span>
              )}
              {zusatzSpalten.map((sp) => (
                <span
                  key={sp.key}
                  className={mergeClasses(s.zelle, sp.mittig && s.zelleMittig, titelText(sp.titel) && s.mitTitel)}
                  data-titel={titelText(sp.titel)}
                >
                  {sp.zelle(z)}
                </span>
              ))}
              <span className={s.aktionen} data-zelle="aktionen">
                {z.extra}
                {z.onBearbeiten && (
                  <SofortTipp text={z.inlineAktiv ? "Bearbeiten beenden" : "Bearbeiten"} ausrichtung="rechts">
                    <AnsichtsKnopf
                      label={z.inlineAktiv ? "Bearbeiten beenden" : "Bearbeiten"}
                      // Kein natives title: der SofortTipp zeigt den Text schon.
                      title=""
                      className={mergeClasses(s.icon, z.inlineAktiv && s.iconAktiv)}
                      onClick={(el) => z.onBearbeiten!({ nurAnsehen: imGesperrtenFormular(el) })}
                    >
                      {StiftIcon}
                    </AnsichtsKnopf>
                  </SofortTipp>
                )}
                {z.onEntfernen && (
                  <SofortTipp text="Entfernen (wirkt beim Speichern)" ausrichtung="rechts">
                    <button
                      type="button"
                      className={mergeClasses(s.icon, s.iconWeg)}
                      aria-label="Entfernen"
                      onClick={z.onEntfernen}
                    >
                      {EntfernenIcon}
                    </button>
                  </SofortTipp>
                )}
                {(() => {
                  const loeschen = loeschenVon(z);
                  return loeschen ? (
                    <SofortTipp text="Kontakt löschen" ausrichtung="rechts">
                      <button
                        type="button"
                        className={mergeClasses(s.icon, s.iconWeg)}
                        aria-label="Kontakt löschen"
                        onClick={loeschen}
                      >
                        {EimerIcon}
                      </button>
                    </SofortTipp>
                  ) : null;
                })()}
              </span>
            </ZeilenRahmen>
          ))}
        </div>
        {ueber && ablage?.hinweis && <div className={s.ablageHinweis}>{ablage.hinweis}</div>}
        {abgelehnt && (
          <div className={s.ablageAbgelehntHinweis} role="status">
            {abgelehnt}
          </div>
        )}
      </div>
    );

  if (!titel && !aktion) return leerOderTabelle;
  return (
    <section className={s.sektion}>
      <div className={s.kopf}>
        <div className={s.kopfText}>
          {titel && <h4 className={s.titel}>{titel}</h4>}
          {untertitel && <p className={s.untertitel}>{untertitel}</p>}
        </div>
        {aktion}
      </div>
      {leerOderTabelle}
    </section>
  );
}
